import React from 'react';

interface NetworkBackgroundProps {
  className?: string;
}

export function NetworkBackground({ className = '' }: NetworkBackgroundProps) {
  // Generate more nodes with smaller sizes for better network effect
  const nodes = [
    { id: 0, x: 15, y: 20, size: 0.5 },
    { id: 1, x: 35, y: 15, size: 0.5 },
    { id: 2, x: 65, y: 25, size: 0.5 },
    { id: 3, x: 85, y: 18, size: 0.5 },
    { id: 4, x: 25, y: 45, size: 0.5 },
    { id: 5, x: 55, y: 40, size: 0.5 },
    { id: 6, x: 75, y: 50, size: 0.5 },
    { id: 7, x: 20, y: 70, size: 0.5 },
    { id: 8, x: 45, y: 65, size: 0.5 },
    { id: 9, x: 70, y: 75, size: 0.5 },
    { id: 10, x: 90, y: 80, size: 0.5 },
    { id: 11, x: 10, y: 90, size: 0.5 },
    { id: 12, x: 50, y: 10, size: 0.5 },
    { id: 13, x: 30, y: 30, size: 0.5 },
    { id: 14, x: 80, y: 35, size: 0.5 },
    { id: 15, x: 40, y: 55, size: 0.5 },
    { id: 16, x: 60, y: 60, size: 0.5 },
    { id: 17, x: 15, y: 60, size: 0.5 },
    { id: 18, x: 85, y: 65, size: 0.5 },
    { id: 19, x: 50, y: 85, size: 0.5 },
    { id: 20, x: 5, y: 50, size: 0.5 },
    { id: 21, x: 95, y: 45, size: 0.5 },
    { id: 22, x: 25, y: 85, size: 0.5 },
    { id: 23, x: 75, y: 15, size: 0.5 },
    // Additional nodes with focus on top area
    { id: 24, x: 12, y: 35, size: 0.5 },
    { id: 25, x: 42, y: 22, size: 0.5 },
    { id: 26, x: 68, y: 12, size: 0.5 },
    { id: 27, x: 88, y: 28, size: 0.5 },
    { id: 28, x: 32, y: 52, size: 0.5 },
    { id: 29, x: 58, y: 48, size: 0.5 },
    { id: 30, x: 78, y: 42, size: 0.5 },
    { id: 31, x: 18, y: 78, size: 0.5 },
    { id: 32, x: 48, y: 72, size: 0.5 },
    { id: 33, x: 72, y: 68, size: 0.5 },
    { id: 34, x: 92, y: 72, size: 0.5 },
    { id: 35, x: 8, y: 82, size: 0.5 },
    { id: 36, x: 52, y: 95, size: 0.5 },
    { id: 37, x: 28, y: 18, size: 0.5 },
    { id: 38, x: 82, y: 22, size: 0.5 },
    { id: 39, x: 38, y: 38, size: 0.5 },
    { id: 40, x: 62, y: 32, size: 0.5 },
    { id: 41, x: 22, y: 58, size: 0.5 },
    { id: 42, x: 88, y: 52, size: 0.5 },
    { id: 43, x: 35, y: 78, size: 0.5 },
    { id: 44, x: 65, y: 82, size: 0.5 },
    { id: 45, x: 2, y: 25, size: 0.5 },
    { id: 46, x: 98, y: 15, size: 0.5 },
    { id: 47, x: 45, y: 5, size: 0.5 },
    // Extra nodes for top area coverage
    { id: 48, x: 20, y: 8, size: 0.5 },
    { id: 49, x: 60, y: 5, size: 0.5 },
    { id: 50, x: 80, y: 8, size: 0.5 },
    { id: 51, x: 10, y: 12, size: 0.5 },
    { id: 52, x: 90, y: 10, size: 0.5 },
    { id: 53, x: 25, y: 3, size: 0.5 },
    { id: 54, x: 70, y: 6, size: 0.5 },
    { id: 55, x: 5, y: 18, size: 0.5 },
    { id: 56, x: 95, y: 20, size: 0.5 },
    { id: 57, x: 40, y: 8, size: 0.5 },
    { id: 58, x: 15, y: 5, size: 0.5 },
    { id: 59, x: 85, y: 5, size: 0.5 },
  ];

  // Generate connections between nearby nodes
  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const distance = Math.sqrt(
        Math.pow(nodes[i].x - nodes[j].x, 2) + Math.pow(nodes[i].y - nodes[j].y, 2)
      );
      // Connect nodes that are within a certain distance
      if (distance < 20) {
        connections.push({
          from: nodes[i],
          to: nodes[j],
          opacity: Math.max(0.3, 1 - distance / 20), // Adjusted for smaller distance
        });
      }
    }
  }

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Test visibility with a simple background pattern */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, #E3EFFF 2px, transparent 2px),
                           radial-gradient(circle at 80% 80%, #E3EFFF 1px, transparent 1px)`,
          backgroundSize: '50px 50px, 30px 30px'
        }}
      />
      
      <svg
        className="w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Define gradient for subtle depth */}
        <defs>
          <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#E3EFFF" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#E3EFFF" stopOpacity="0.3" />
          </radialGradient>
        </defs>

        {/* Render connections FIRST so they appear behind nodes */}
        {connections.map((connection, index) => (
          <line
            key={index}
            x1={connection.from.x}
            y1={connection.from.y}
            x2={connection.to.x}
            y2={connection.to.y}
            stroke="#E3EFFF"
            strokeWidth="0.15"
            strokeOpacity={connection.opacity}
          />
        ))}

        {/* Render nodes */}
        {nodes.map((node) => (
          <circle
            key={node.id}
            cx={node.x}
            cy={node.y}
            r={node.size}
            fill="#E3EFFF"
            fillOpacity="0.6"
          />
        ))}
      </svg>
    </div>
  );
} 